/**
 * EL GUION DEL RECORRIDO: una parada por pantalla, no por elemento.
 *
 * Cesar, 2026-10-01: «necesito una guía como Deel lo hace, en donde te lleva
 * pantalla por pantalla comentándote qué encuentras en cada cosa y con botón de
 * siguiente, siguiente… debería de pasearme por toda la herramienta».
 *
 * Y el 2026-10-02: «la guía por la plataforma por parte de Marty que te guía
 * punto por punto debería de ser más corta». Tenía 47 paradas: una por cada
 * pestaña, cada filtro y cada panel. Nadie llega a la 47. Ahora son VEINTE,
 * una por pantalla, y cada una dice qué contesta esa pantalla y qué se puede
 * hacer en ella. Lo que la pantalla ya explica sola —sus pestañas, sus
 * filtros— no se repite aquí.
 *
 * CÓMO SE SEÑALA UN ELEMENTO
 * ---------------------------
 * `objetivo` es un selector CSS, casi siempre un `data-guia` que ponen los
 * componentes comunes (riel, pestañas, filtros, cifras, paneles…). `texto`
 * afina cuando hay varios del mismo tipo: se elige el que CONTIENE esa frase.
 *
 * Si el elemento no está —una pantalla vacía, un rol que no lo ve—, el globo
 * sale centrado y sin foco. `requiere` es un selector que tiene que existir
 * para que el paso se enseñe: así se saltan las secciones que el rol no ve.
 *
 * LA VOZ ES LA DE MARTY: primera persona, frases cortas. Ver `lib/marty.ts`.
 */

export type Capitulo =
  | "Bienvenida"
  | "Lo básico"
  | "Dashboard"
  | "Finanzas"
  | "Contabilidad"
  | "Holding"
  | "Control"

export interface Paso {
  capitulo: Capitulo
  /** Pantalla en la que vive el paso. Se navega a ella al llegar. */
  ruta: string
  /** Selector del elemento a iluminar. Sin él, el globo va centrado. */
  objetivo?: string
  /** Entre los que casan con `objetivo`, el que contiene esta frase. */
  texto?: string
  /** El paso sólo se enseña si esto existe en la página. */
  requiere?: string
  titulo: string
  /** Qué es y para qué sirve. */
  cuerpo: string
  /** Qué se puede hacer aquí, o dónde se cambia. Opcional. */
  accion?: string
}

const HOLDING = '[data-guia="menu-holding"]'
const AMBAS = '[data-guia="pestana-cuadres"]'

export const PASOS: Paso[] = [
  // ------------------------------------------------------------ bienvenida
  {
    capitulo: "Bienvenida",
    ruta: "/dashboard",
    titulo: "Hola, soy Marty",
    cuerpo: "Soy tu contador y tu CFO. Te llevo por la herramienta en veinte paradas, una por pantalla: qué contesta cada una y qué puedes hacer en ella.",
    accion: "Avanza con «Siguiente» o con la flecha → del teclado. Puedes saltar a un capítulo desde abajo y salir con Esc.",
  },

  // ------------------------------------------------------------ lo básico
  {
    capitulo: "Lo básico",
    ruta: "/dashboard",
    objetivo: '[data-guia="riel"]',
    titulo: "El menú",
    cuerpo: "Finanzas es el modelo de gestión, con lo que decides. Contabilidad es lo que se firma y se declara. Holding es el grupo entero. Sólo ves lo que tu rol usa.",
  },
  {
    capitulo: "Lo básico",
    ruta: "/dashboard",
    objetivo: '[data-testid="selector-sociedad"]',
    titulo: "Cliente y sociedad",
    cuerpo: "Arriba eliges de qué empresa hablas: el cliente, y dentro de él la sociedad —Perú, México, Brasil, Panamá— o todas a la vez. Todo lo de abajo es de esa elección.",
    accion: "Al lado tienes el idioma y el tema. Los nombres del fisco —IGV, SAT, Receita— no se traducen: son los de cada país.",
  },
  {
    capitulo: "Lo básico",
    ruta: "/dashboard",
    objetivo: '[data-guia="marty"]',
    titulo: "Pregúntame",
    cuerpo: "Desde cualquier pantalla me preguntas por tus cifras: «¿por qué bajó el margen en mayo?». Contesto con los datos de ese momento y te digo cuáles usé. Si no lo tengo, te lo digo.",
  },

  // ------------------------------------------------------------ dashboard
  {
    capitulo: "Dashboard",
    ruta: "/dashboard",
    objetivo: '[data-guia="cifras"]',
    titulo: "Cómo va el negocio",
    cuerpo: "La portada: ventas, costos, EBITDA y caja del período, cada una con su peso sobre las ventas, y si algo requiere tu atención, va en rojo y es una sola cosa.",
    accion: "Aquí no se edita nada. Si una cifra te extraña, el camino es Finanzas o Contabilidad, donde cada cambio pide motivo. Abajo, Primeros pasos: lo que falta para que todo funcione.",
  },

  // ------------------------------------------------------------ finanzas
  {
    capitulo: "Finanzas",
    ruta: "/finanzas/pnl",
    objetivo: '[data-guia="panel"]',
    texto: "Estado de resultados",
    titulo: "Finanzas: el P&L",
    cuerpo: "Todo en dólares. Mes a mes y línea a línea, con la línea roja que separa lo REAL de lo proyectado. Los filtros de arriba —año, sociedad, período— mandan sobre todas las pestañas.",
    accion: "Arriba a la derecha eliges de dónde salen los meses futuros —tu hoja o lo que tienes declarado— y lo descargas en Excel.",
  },
  {
    capitulo: "Finanzas",
    ruta: "/finanzas/caja",
    objetivo: '[data-guia="panel"]',
    titulo: "Flujo de caja",
    cuerpo: "El P&L dice si ganas; la caja dice si llegas a fin de mes. Un mes puede tener EBITDA positivo y caja negativa, y esta pantalla existe para verlo.",
  },
  {
    capitulo: "Finanzas",
    ruta: "/finanzas/forecast",
    objetivo: '[data-guia="panel"]',
    titulo: "Forecast",
    cuerpo: "Los tres escenarios a la vez —pesimista, base y optimista—, la caja y los cobros proyectados. Tu hoja es el escenario base; los otros dos lo acotan.",
    accion: "Ajustas los supuestos de cada escenario o pisas un mes concreto. En «Cómo se arma el forecast» lo ves por capas y por cliente, desde tus contratos, y declaras lo nuevo.",
  },
  {
    capitulo: "Finanzas",
    ruta: "/finanzas/desglose",
    objetivo: '[data-guia="cuenta-desglosada"]',
    titulo: "Desglose",
    cuerpo: "Ventas, costo de ventas, gastos y costo de equipo, línea por línea, cada una con su peso sobre las ventas del mismo período: para ver qué crece más rápido que el negocio.",
    accion: "Abre una línea para verla por país. Haz clic en un importe para corregirlo: tu hoja no se toca y la corrección queda en la auditoría.",
  },
  {
    capitulo: "Finanzas",
    ruta: "/finanzas/cobranzas",
    objetivo: '[data-guia="panel"]',
    titulo: "Cobranzas",
    cuerpo: "Quién te debe, desde cuándo y a quién le toca aviso hoy. Desde cada fila mandas el aviso, lo pausas si lo estás hablando tú, o la das por incobrable.",
    accion: "«Cambiar cuándo aviso» fija la cadencia; «El texto de los avisos» fija qué digo, en el idioma de quien lo recibe.",
  },
  {
    capitulo: "Finanzas",
    ruta: "/finanzas/cuadres",
    requiere: AMBAS,
    objetivo: '[data-guia="pestanas"]',
    titulo: "Cuadres, bancos y qué va a Finanzas",
    cuerpo: "Cuadres: tu hoja contra lo facturado y contra el libro; cuando no cuadran, te digo dónde. Bancos y tipos de cambio: los dos datos de base. Qué va a Finanzas: la fecha desde la que Finanzas deja de leer tu hoja histórica y lee el maestro y el libro.",
  },

  // ------------------------------------------------------------ contabilidad
  {
    capitulo: "Contabilidad",
    ruta: "/contabilidad/revision",
    objetivo: '[data-guia="panel"]',
    titulo: "Contabilidad: la cola de revisión",
    cuerpo: "Aquí todo es por sociedad y en su moneda. Los comprobantes que no pude resolver solo, con mi propuesta de cuenta y cuánta confianza le tengo. Debajo, lo que llegó sin su comprobante fiscal.",
    accion: "Aprobar confirma mi propuesta; corregir la cambia y me enseña. A la tercera corrección del mismo proveedor dejo de preguntarte.",
  },
  {
    capitulo: "Contabilidad",
    ruta: "/contabilidad/libros/diario",
    objetivo: '[data-guia="pestanas"]',
    texto: "Diario",
    titulo: "Los libros",
    cuerpo: "Diario, mayor —con cada asiento a un clic—, balance de comprobación y comparar libros. Cada uno en los tres libros: societario, tributario y gerencial.",
    accion: "Un asiento se corrige con otro asiento, nunca borrando: el libro es una pista y las pistas no se reescriben.",
  },
  {
    capitulo: "Contabilidad",
    ruta: "/contabilidad/estados/situacion",
    objetivo: '[data-guia="pestanas"]',
    texto: "Balance general",
    titulo: "Estados contables",
    cuerpo: "Balance general, estado de resultados y flujo de efectivo, como se presentan y desde los asientos, con su cuadre visible arriba. No son los de Finanzas: éstos tienen que cuadrar.",
  },
  {
    capitulo: "Contabilidad",
    ruta: "/contabilidad/planilla",
    objetivo: '[data-guia="pestana-planilla"]',
    titulo: "Planilla",
    cuerpo: "La planilla del período, persona por persona: bruto, descuentos, aportes del empleador y neto, y qué parte no sale de caja este mes. Y la descarga de las boletas.",
  },
  {
    capitulo: "Contabilidad",
    ruta: "/contabilidad/maestros/sociedades",
    objetivo: '[data-guia="pestanas"]',
    texto: "Sociedades",
    titulo: "Maestros",
    cuerpo: "Las fichas de las que depende todo: sociedades y su régimen, el plan del P&L, los períodos abiertos, clientes, proveedores, contratos, personas y las reglas de cada libro. Un cambio aquí mueve de verdad las cifras.",
    accion: "Clientes y proveedores se activan y desactivan sin borrar nada. Y «Carga por archivo» trae en bloque lo que no quieras teclear.",
  },
  {
    capitulo: "Contabilidad",
    ruta: "/contabilidad/expediente",
    objetivo: '[data-guia="pestana-expediente"]',
    titulo: "Declaraciones y expediente",
    cuerpo: "Declaraciones es donde el contador revisa el período —el cuadre, las excepciones, las casillas— y firma; la firma se bloquea si no cuadra. Expediente guarda los papeles de la empresa y las declaraciones tal como se presentaron.",
    accion: "Desde Declaraciones se descargan los archivos oficiales para el portal de cada país.",
  },

  // ------------------------------------------------------------ holding
  {
    capitulo: "Holding",
    ruta: "/holding/gestion/pnl",
    requiere: HOLDING,
    objetivo: '[data-guia="pestanas"]',
    titulo: "Holding: el grupo como una sola empresa",
    cuerpo: "Gestión es el P&L, la caja, el forecast y las cobranzas de todas las sociedades sumadas en USD. Consolidado es lo contable: con lo que se facturan entre ellas eliminado, su evolución, lo intercompañía, la caja disponible por sociedad y los impuestos del grupo.",
    accion: "Al pie del consolidado declaras quién es dueño de quién: de eso depende cómo se consolida.",
  },

  // ------------------------------------------------------------ control
  {
    capitulo: "Control",
    ruta: "/pendientes",
    objetivo: '[data-guia="menu-pendientes"]',
    titulo: "Pendientes, auditoría y accesos",
    cuerpo: "Pendientes: lo que alguien marcó como que no cuadra, al lado de la cifra. Auditoría: quién cambió qué, cuándo y con qué motivo; no se edita. Accesos: quién entra y con qué alcance.",
    accion: "Invita a tu contador: el cierre exige que quien prepara un período no sea quien lo firma.",
  },
  {
    capitulo: "Control",
    ruta: "/dashboard",
    objetivo: '[data-guia="menu-usuario"]',
    titulo: "Eso es todo",
    cuerpo: "Ya conoces la herramienta. Si quieres volver a verla, está en tu menú: «Ver la guía de la plataforma».",
    accion: "Y para cualquier duda sobre tus cifras, pregúntame.",
  },
]

export const CAPITULOS: Capitulo[] = [
  "Bienvenida", "Lo básico", "Dashboard", "Finanzas", "Contabilidad", "Holding", "Control",
]
